/**
 * Component for managing product-addon relationships
 * Changes are made locally and saved via parent form's Save button
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  Package,
  Star
} from 'lucide-react';
import {
  getLinkedAddons,
  getAvailableAddons,
} from '../services/productAddons.service';

interface AddonChange {
  id?: string; // undefined for new addons
  addon_id: string;
  recommended: boolean;
  addon_name: string;
  addon_price: number | null;
  picture_url: string | null;
  active: boolean;
  _status: 'existing' | 'added' | 'removed'; // Track change type
}

interface Props {
  productTypeId: string;
  merchantId: string;
  locationId?: string;
  currency: string;
  onChange: (addons: AddonChange[]) => void;
}

export function ProductAddonsManager({ productTypeId, merchantId, locationId, currency, onChange }: Props) {
  const { t } = useTranslation();
  const [addons, setAddons] = useState<AddonChange[]>([]);
  const [availableAddons, setAvailableAddons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedAddonId, setSelectedAddonId] = useState<string>('');
  const [selectedRecommended, setSelectedRecommended] = useState(false);

  useEffect(() => {
    loadLinkedAddons();
  }, [productTypeId]);

  async function loadLinkedAddons() {
    setLoading(true);
    setError(null);
    const { data, error: err } = await getLinkedAddons(productTypeId);
    if (err) {
      setError(err);
    } else {
      const initialAddons: AddonChange[] = (data || []).map(addon => ({
        id: addon.id,
        addon_id: addon.addon_id,
        recommended: addon.recommended,
        addon_name: addon.addon_name,
        addon_price: addon.addon_price,
        picture_url: addon.picture_url,
        active: addon.active,
        _status: 'existing' as const
      }));
      setAddons(initialAddons);
      onChange(initialAddons);
    }
    setLoading(false);
  }

  async function loadAvailableAddons() {
    const { data, error: err } = await getAvailableAddons(merchantId, productTypeId, locationId);
    if (err) {
      setError(err);
    } else {
      // Filter out addons that are already in local state (including newly added ones)
      const currentAddonIds = addons
        .filter(a => a._status !== 'removed')
        .map(a => a.addon_id);
      const filtered = (data || []).filter(addon => !currentAddonIds.includes(addon.id));
      setAvailableAddons(filtered);
    }
  }

  function openLinkModal() {
    setSelectedAddonId('');
    setSelectedRecommended(false);
    setShowLinkModal(true);
    loadAvailableAddons();
  }

  function closeLinkModal() {
    setShowLinkModal(false);
    setSelectedAddonId('');
    setSelectedRecommended(false);
  }

  function handleAddAddon() {
    if (!selectedAddonId) {
      setError(t('productAddons.pleaseSelectAddon'));
      return;
    }

    const selectedAddon = availableAddons.find(a => a.id === selectedAddonId);
    if (!selectedAddon) return;

    const newAddon: AddonChange = {
      addon_id: selectedAddonId,
      recommended: selectedRecommended,
      addon_name: selectedAddon.product_name,
      addon_price: selectedAddon.price,
      picture_url: selectedAddon.picture_url,
      active: selectedAddon.active,
      _status: 'added'
    };

    const updated = [...addons, newAddon];
    setAddons(updated);
    onChange(updated);
    closeLinkModal();
  }

  function handleToggleRecommended(addonId: string) {
    const updated = addons.map(addon =>
      addon.addon_id === addonId
        ? { ...addon, recommended: !addon.recommended }
        : addon
    );
    setAddons(updated);
    onChange(updated);
  }

  function handleRemoveAddon(addonId: string) {
    const updated = addons.map(addon => {
      if (addon.addon_id === addonId) {
        // If it was newly added, remove it completely
        if (addon._status === 'added') {
          return null;
        }
        // If it's existing, mark as removed
        return { ...addon, _status: 'removed' as const };
      }
      return addon;
    }).filter(Boolean) as AddonChange[];

    setAddons(updated);
    onChange(updated);
  }

  function formatPrice(price: number | null): string {
    if (price === null) return '';
    return `${currency} ${price.toFixed(2)}`;
  }

  // Filter addons for display (exclude removed ones)
  const visibleAddons = addons.filter(addon => addon._status !== 'removed');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-xl">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
        <span className="ml-md text-secondary">{t('productAddons.loadingLinkedAddons')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-lg">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-primary">{t('productAddons.linkedAddons')}</h3>
          <p className="text-sm text-secondary">
            {t('productAddons.addonsAvailable', { count: visibleAddons.length })}
          </p>
        </div>
        <button onClick={openLinkModal} className="btn btn-primary">
          <Plus className="w-4 h-4" />
          {t('productAddons.linkAddon')}
        </button>
      </div>

      {error && (
        <div className="p-md bg-red-50 border border-red-200 rounded-lg flex items-center gap-sm text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {visibleAddons.length === 0 ? (
        <div className="text-center py-xl border-2 border-dashed border-primary rounded-lg">
          <Package className="w-12 h-12 text-secondary mx-auto mb-md" />
          <p className="text-secondary mb-md">{t('productAddons.noAddonsLinked')}</p>
          <button onClick={openLinkModal} className="btn btn-primary">
            <Plus className="w-4 h-4" />
            {t('productAddons.linkFirstAddon')}
          </button>
        </div>
      ) : (
        <div className="space-y-sm">
          {visibleAddons.map(addon => (
            <div
              key={addon.addon_id}
              className={`border rounded-lg p-md transition-colors ${
                addon._status === 'added'
                  ? 'border-green-300 bg-green-50'
                  : 'border-primary bg-primary hover:bg-secondary'
              }`}
            >
              <div className="flex items-center gap-md">
                {addon.picture_url ? (
                  <img
                    src={addon.picture_url}
                    alt={addon.addon_name}
                    className="w-12 h-12 rounded object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-secondary flex items-center justify-center">
                    <Package className="w-6 h-6 text-secondary" />
                  </div>
                )}

                <div className="flex-1">
                  <div className="flex items-center gap-sm">
                    <h4 className="font-medium text-primary">{addon.addon_name}</h4>
                    {addon.recommended && (
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    )}
                    {!addon.active && (
                      <span className="px-sm py-xs text-xs bg-amber-200 text-amber-800 rounded">
                        {t('common.inactive')}
                      </span>
                    )}
                    {addon._status === 'added' && (
                      <span className="px-sm py-xs text-xs bg-green-600 text-white rounded">
                        {t('productAddons.new')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-secondary">{formatPrice(addon.addon_price)}</p>
                </div>

                <div className="flex items-center gap-xs">
                  <button
                    onClick={() => handleToggleRecommended(addon.addon_id)}
                    className={`px-md py-sm text-sm rounded-md border transition-colors ${
                      addon.recommended
                        ? 'bg-yellow-500 text-white border-yellow-500 hover:bg-yellow-600'
                        : 'bg-tertiary text-secondary border-primary hover:bg-secondary'
                    }`}
                    title={addon.recommended ? t('productAddons.markAsNotRecommended') : t('productAddons.markAsRecommended')}
                  >
                    <Star className={`w-4 h-4 ${addon.recommended ? 'fill-white' : ''}`} />
                  </button>

                  <button
                    onClick={() => handleRemoveAddon(addon.addon_id)}
                    className="p-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    title={t('productAddons.removeAddon')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Link Addon Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-primary rounded-lg shadow-xl max-w-md w-full mx-md">
            <div className="flex items-center justify-between p-lg border-b border-primary">
              <h3 className="text-lg font-semibold text-primary">{t('productAddons.linkAddon')}</h3>
              <button
                onClick={closeLinkModal}
                className="text-secondary hover:text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-lg space-y-lg">
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('productAddons.selectAddon')} <span className="text-danger">*</span>
                </label>
                <select
                  value={selectedAddonId}
                  onChange={(e) => setSelectedAddonId(e.target.value)}
                  className="input w-full"
                >
                  <option value="">{t('productAddons.chooseAddon')}</option>
                  {availableAddons.map(addon => (
                    <option key={addon.id} value={addon.id}>
                      {addon.product_name} {addon.price !== null ? `- ${currency} ${addon.price.toFixed(2)}` : ''}
                    </option>
                  ))}
                </select>
                {availableAddons.length === 0 && (
                  <p className="text-xs text-secondary mt-xs">
                    {t('productAddons.allAddonsLinked')}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-md">
                <input
                  type="checkbox"
                  id="recommended"
                  checked={selectedRecommended}
                  onChange={(e) => setSelectedRecommended(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="recommended" className="text-sm font-medium text-primary">
                  {t('productAddons.markAsRecommended')}
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-md p-lg border-t border-primary">
              <button
                onClick={closeLinkModal}
                className="btn btn-ghost"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAddAddon}
                className="btn btn-primary"
                disabled={!selectedAddonId}
              >
                <Plus className="w-4 h-4" />
                {t('productAddons.addAddon')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
